import React from 'react';
import { motion } from 'framer-motion';
import { Tv, Users, Wifi, ExternalLink, Calendar, Music, Trophy, Target, Star, Award, Globe, Medal, Flame, Shield } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function Gallery() {
  const {
    t
  } = useLanguage();
  const topScorers = [{
    rank: 1,
    name: 'Olivier Giroud',
    goals: 57
  }, {
    rank: 2,
    name: 'Thierry Henry',
    goals: 51
  }, {
    rank: 3,
    name: 'Kylian Mbappé',
    goals: 48
  }, {
    rank: 4,
    name: 'Antoine Griezmann',
    goals: 46
  }, {
    rank: 5,
    name: 'Michel Platini',
    goals: 41
  }, {
    rank: 6,
    name: 'Karim Benzema',
    goals: 37
  }, {
    rank: 7,
    name: 'David Trezeguet',
    goals: 34
  }, {
    rank: 8,
    name: 'Zinedine Zidane',
    goals: 31
  }, {
    rank: 9,
    name: 'Just Fontaine',
    goals: 30
  }, {
    rank: 10,
    name: 'Jean-Pierre Papin',
    goals: 30
  }];
  const records = [{
    icon: <Trophy size={24} className="text-france-gold" data-id="element-93" />,
    title: 'Biggest Win',
    value: '10–0',
    subtitle: 'vs Azerbaijan (1995)'
  }, {
    icon: <Star size={24} className="text-france-gold" data-id="element-94" />,
    title: 'Most Capped',
    value: '145',
    subtitle: 'Hugo Lloris'
  }, {
    icon: <Award size={24} className="text-france-gold" data-id="element-95" />,
    title: 'Trophy Cabinet',
    value: '4 Titles',
    subtitle: 'WC 98, WC 18, Euro 84, Euro 00'
  }, {
    icon: <Target size={24} className="text-france-gold" data-id="element-96" />,
    title: 'Golden Era',
    value: '3 Legends',
    subtitle: 'Platini, Zidane, Henry'
  }, {
    icon: <Globe size={24} className="text-france-gold" data-id="element-97" />,
    title: 'FIFA Ranking',
    value: 'Top 3',
    subtitle: 'Consistently world elite'
  }, {
    icon: <Medal size={24} className="text-france-gold" data-id="element-98" />,
    title: 'World Cup Best',
    value: 'Champions',
    subtitle: '1998, 2018'
  }, {
    icon: <Flame size={24} className="text-france-gold" data-id="element-99" />,
    title: 'All-Time Wins',
    value: '700+',
    subtitle: 'Since 1904'
  }, {
    icon: <Shield size={24} className="text-france-gold" data-id="element-100" />,
    title: 'Consecutive WC',
    value: '7 Straight',
    subtitle: 'Since 1998'
  }];
  const quickLinks = [{
    icon: <Tv size={18} data-id="element-101" />,
    label: t('fanHq.whereToWatch'),
    desc: t('fanHq.whereToWatchDesc'),
    link: 'https://www.foxsports.com/soccer/fifa-world-cup'
  }, {
    icon: <Users size={18} data-id="element-102" />,
    label: t('fanHq.fanZones'),
    desc: t('fanHq.fanZonesDesc'),
    link: 'https://www.fifa.com/en/tournaments/mens/worldcup/canadamexicousa2026/host-cities/fan-information-channels'
  }, {
    icon: <Globe size={18} data-id="element-103" />,
    label: t('fanHq.fifaRanking'),
    desc: t('fanHq.fifaRankingDesc'),
    link: 'https://www.fifa.com/en/world-rankings'
  }, {
    icon: <Calendar size={18} data-id="element-104" />,
    label: t('fanHq.fullSchedule'),
    desc: t('fanHq.fullScheduleDesc'),
    link: 'https://www.espn.com/soccer/schedule/_/league/fifa.world'
  }];
  return <section className="py-12 md:py-24 bg-[#0a0a0a]" id="fan-hq" data-id="element-105">
      <div className="container mx-auto px-4 md:px-12" data-id="element-106">
        {/* Section Header — consolidated */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10 md:mb-16" data-id="element-107">
          <h2 className="text-france-blue font-oswald uppercase tracking-widest text-xs md:text-sm font-bold mb-2" data-id="element-108">
            {t('fanHq.label')}
          </h2>
          <h3 className="text-3xl md:text-5xl font-oswald font-bold uppercase mb-6" data-id="element-109">
            {t('fanHq.title1')}{' '}
            <span className="text-france-gold" data-id="element-110">{t('fanHq.title2')}</span>
          </h3>
        </motion.div>
        {/* Fan Essentials — Anthem + Top Scorers */}
        <div className="mb-8 md:mb-12" data-id="element-111">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="flex items-center gap-3 mb-6" data-id="element-112">
            <Music className="text-france-gold" size={24} data-id="element-113" />
            <h4 className="font-oswald text-2xl md:text-3xl font-bold uppercase text-white" data-id="element-114">
              Fan <span className="text-france-blue" data-id="element-115">Essentials</span>
            </h4>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-116">
            {/* La Marseillaise — National Anthem */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4
          }} className="bg-[#111] border border-france-blue/30 rounded-xl p-6 md:p-8" data-id="element-117">
              <div className="flex items-center gap-2 mb-1 text-france-gold" data-id="element-118">
                <Music size={18} data-id="element-119" />
                <h5 className="font-oswald text-xl md:text-2xl font-bold uppercase text-white" data-id="element-120">
                  La Marseillaise
                </h5>
              </div>
              <p className="text-xs text-france-blue font-oswald uppercase tracking-widest mb-5" data-id="element-121">
                {t('fanHq.nationalAnthem')}
              </p>
              <p className="text-sm md:text-base text-gray-300 whitespace-pre-line italic mb-4 leading-relaxed" data-id="element-122">
                "Allons enfants de la Patrie,{'\n'}Le jour de gloire est arrivé!
                {'\n'}
                Contre nous de la tyrannie,{'\n'}L'étendard sanglant est levé!"
              </p>
              <p className="text-xs text-gray-500 whitespace-pre-line mb-6 leading-relaxed" data-id="element-123">
                Arise, children of the Fatherland,{'\n'}The day of glory has
                arrived!
                {'\n'}Against us, tyranny's{'\n'}Bloody standard is raised!
              </p>
              <a href="https://www.youtube.com/watch?v=UYOp2qdtxkk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-france-blue/20 border border-france-blue/30 text-france-blue hover:bg-france-blue hover:text-white px-5 py-2.5 rounded-lg font-oswald uppercase text-xs font-bold tracking-wider transition-all duration-300 min-h-[44px]" data-id="element-124">
                <ExternalLink size={14} data-id="element-125" />
                {t('fanHq.watchYoutube')}
              </a>
            </motion.div>
            {/* All-Time Top Scorers */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.1
          }} className="bg-[#111] border border-white/5 rounded-xl p-6 md:p-8" data-id="element-126">
              <div className="flex items-center gap-2 mb-5" data-id="element-127">
                <Target size={18} className="text-france-gold" data-id="element-128" />
                <h5 className="font-oswald text-xl md:text-2xl font-bold uppercase text-white" data-id="element-129">
                  All-Time Top <span className="text-france-gold" data-id="element-130">Scorers</span>
                </h5>
              </div>
              <div className="space-y-0" data-id="element-131">
                {topScorers.map((player, i) => <div key={i} className={`flex items-center justify-between py-2.5 ${i < topScorers.length - 1 ? 'border-b border-white/5' : ''}`} data-id="element-132">
                    <div className="flex items-center gap-3" data-id="element-133">
                      <span className="font-oswald text-sm font-bold text-france-gold w-6 text-right" data-id="element-134">
                        {player.rank}
                      </span>
                      <span className={`font-oswald text-sm font-bold uppercase ${i === 0 ? 'text-white' : 'text-gray-300'}`} data-id="element-135">
                        {player.name}
                      </span>
                    </div>
                    <span className={`font-oswald text-sm font-bold ${i === 0 ? 'text-france-gold' : 'text-gray-500'}`} data-id="element-136">
                      {player.goals} goals
                    </span>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
        {/* Record Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-8 md:mb-12" data-id="element-137">
          {records.map((record, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.08
        }} className="bg-[#111] border border-white/5 rounded-xl p-5 md:p-6 hover:border-france-gold/30 transition-colors" data-id="element-138">
              <div className="mb-3" data-id="element-139">{record.icon}</div>
              <p className="text-xs text-france-blue font-oswald uppercase tracking-widest mb-1" data-id="element-140">
                {record.title}
              </p>
              <p className="font-oswald text-2xl md:text-3xl font-bold text-white mb-1" data-id="element-141">
                {record.value}
              </p>
              <p className="text-xs text-gray-500" data-id="element-142">{record.subtitle}</p>
            </motion.div>)}
        </div>
        {/* Quick Links Grid */}
        <div data-id="element-143">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="flex items-center gap-3 mb-6" data-id="element-144">
            <ExternalLink className="text-france-gold" size={24} data-id="element-145" />
            <h4 className="font-oswald text-2xl md:text-3xl font-bold uppercase text-white" data-id="element-146">
              {t('fanHq.quickLinks')}{' '}
              <span className="text-france-blue" data-id="element-147">
                {t('fanHq.quickLinksHighlight')}
              </span>
            </h4>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-148">
            {quickLinks.map((item, index) => <motion.a key={index} href={item.link} target="_blank" rel="noopener noreferrer" initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: index * 0.08
          }} className="bg-[#111] border border-white/5 rounded-xl p-4 md:p-5 hover:border-france-gold/30 transition-all group flex flex-col min-h-[44px]" data-id="element-149">
                <div className="text-france-gold mb-2 group-hover:scale-110 transition-transform origin-left" data-id="element-150">
                  {item.icon}
                </div>
                <h5 className="font-oswald text-xs md:text-sm font-bold uppercase text-white mb-1 leading-tight" data-id="element-151">
                  {item.label}
                </h5>
                <p className="text-[10px] md:text-xs text-gray-500 flex-grow" data-id="element-152">
                  {item.desc}
                </p>
                <ExternalLink size={12} className="text-gray-600 mt-2" data-id="element-153" />
              </motion.a>)}
          </div>
        </div>
      </div>
    </section>;
}